import { isPlatformError } from '@agentic/contracts';
import { clsx } from 'clsx';
import { Bell, CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-react';
import { Toast as RToast } from 'radix-ui';
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { IdText } from './data.tsx';
import { DOT_TONES, type Tone } from './primitives.tsx';

export interface ToastInput {
  /** First line: for an error, the problem in one sentence. */
  title: string;
  /** Second line: for an error, what it means for the user's work. */
  description?: string;
  /** Third line: for an error, what the user can do now. */
  next?: string;
  /** Server reference of a failed request, shown as `Ref: <id>` with a copy button. */
  traceId?: string;
  /** PlatformError code, kept in a collapsed "Technical details" disclosure. */
  code?: string;
  tone?: Tone;
  durationMs?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

export interface ToastApi {
  push(toast: ToastInput): void;
  /**
   * Toast for any thrown value: a generic title, the code under "Technical details" and the reference when the error
   * carries one. It never shows the error's message; apps that know better wording `push` it instead.
   * @deprecated The workbench words errors through its presentation dictionary (`errorToast` + `push`); no caller
   * left. Kept for backward compatibility of the ui-kit API.
   */
  error(error: unknown, title?: string): void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * Status colour of a toast, the same scale as every status badge (`StatusKind`): green done, orange caution, red
 * problem, teal progress, blue action, gray neutral. The colour is carried three times so it reads at a glance:
 * a solid stripe down the left edge, a tinted icon chip, and the icon's shape (never colour alone).
 */
const TONE_CHIP: Record<Tone, string> = {
  neutral: 'bg-surface-2 text-ink-2',
  info: 'bg-info-soft text-info-ink',
  ok: 'bg-ok-soft text-ok-ink',
  warn: 'bg-warn-soft text-warn-ink',
  bad: 'bg-bad-soft text-bad-ink',
  accent: 'bg-accent-soft text-accent-ink',
};

const TONE_ICON: Record<Tone, typeof Info> = {
  neutral: Bell,
  info: Info,
  ok: CircleCheck,
  warn: TriangleAlert,
  bad: CircleAlert,
  accent: Info,
};

/** Toasts that interrupt (errors, cautions) are announced assertively by Radix; the rest politely. */
const ATTENTION_TONES: ReadonlySet<Tone> = new Set<Tone>(['bad', 'warn']);

/**
 * Provides `useToast()`. Toasts stack top-right (newest on top), slide in from the right edge, pause while hovered
 * or focused, and can be swiped away. Errors stay longer and are announced assertively by Radix.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const push = useCallback((toast: ToastInput) => {
    setItems((prev) => [...prev.slice(-4), { ...toast, id: Date.now() + Math.random() }]);
  }, []);
  const api = useMemo<ToastApi>(
    () => ({
      push,
      error(error: unknown, title?: string) {
        const traceId = isPlatformError(error) ? error.details?.traceId : undefined;
        push({
          title: title ?? (isPlatformError(error) ? 'Request failed' : 'Something went wrong'),
          code: isPlatformError(error) ? error.code : 'internal',
          ...(typeof traceId === 'string' && traceId ? { traceId } : {}),
          tone: 'bad',
        });
      },
    }),
    [push],
  );
  return (
    <ToastContext.Provider value={api}>
      <RToast.Provider swipeDirection="right" duration={6000}>
        {children}
        {[...items].reverse().map((t) => {
          const tone = t.tone ?? 'neutral';
          const Icon = TONE_ICON[tone];
          return (
            <RToast.Root
              key={t.id}
              type={ATTENTION_TONES.has(tone) ? 'foreground' : 'background'}
              duration={t.durationMs ?? (tone === 'bad' ? 9000 : 5000)}
              onOpenChange={(open) => {
                if (!open) setItems((prev) => prev.filter((x) => x.id !== t.id));
              }}
              className="wb-toast relative flex items-start gap-3 overflow-hidden rounded-xl border border-line bg-surface py-3.5 pr-10 pl-5 shadow-elevated"
              data-testid="toast"
              data-tone={tone}
            >
              <span aria-hidden className={clsx('absolute inset-y-0 left-0 w-1', DOT_TONES[tone])} />
              <span
                aria-hidden
                className={clsx(
                  'mt-px flex size-7 shrink-0 items-center justify-center rounded-full',
                  TONE_CHIP[tone],
                )}
              >
                <Icon className="size-4" strokeWidth={2.25} />
              </span>
              <div className="min-w-0 flex-1">
                <RToast.Title className="text-[14px] leading-5 font-semibold text-ink">
                  {t.title}
                </RToast.Title>
                {(t.description || t.next) && (
                  <RToast.Description asChild>
                    <div className="mt-1 text-[13px] leading-[18px] text-ink-2">
                      {t.description && <p>{t.description}</p>}
                      {t.next && <p data-testid="toast-next">{t.next}</p>}
                    </div>
                  </RToast.Description>
                )}
                {t.traceId && <ToastReference traceId={t.traceId} />}
                {t.code && (
                  <details className="mt-1 text-[12px] text-ink-3">
                    <summary className="cursor-pointer select-none">Technical details</summary>
                    <code className="font-mono text-[12px]">{t.code}</code>
                  </details>
                )}
              </div>
              <RToast.Close
                aria-label="Dismiss"
                className="absolute top-2.5 right-2.5 inline-flex size-7 items-center justify-center rounded-full text-ink-3 transition-colors hover:bg-surface-2 hover:text-ink"
              >
                <X className="size-4" aria-hidden />
              </RToast.Close>
            </RToast.Root>
          );
        })}
        <RToast.Viewport
          label="Notifications"
          className="fixed top-4 right-4 z-[60] flex w-[min(26rem,calc(100vw-2rem))] flex-col gap-2.5 outline-none"
        />
      </RToast.Provider>
    </ToastContext.Provider>
  );
}

/** `Ref: <id>` line with a copy button; the label for readers and screen readers is "Reference". */
function ToastReference({ traceId }: { traceId: string }) {
  return (
    <p
      className="mt-1 flex items-center gap-1 text-[12px] text-ink-3"
      title="Reference"
      data-testid="toast-reference"
    >
      <span>Ref:</span>
      <IdText value={traceId} copy label="reference" className="text-[12px]" />
    </p>
  );
}

/** Access the toast API. Must be inside `ToastProvider`. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
